import React, { useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Check, Plus, Minus, ArrowLeft, Sparkles } from 'lucide-react';
import { SERVICE_DETAILS, COMING_SOON_SLUGS } from '../data/serviceDetails';
import WaitlistForm from '../components/WaitlistForm';

const ServiceDetail = () => {
  const { slug } = useParams();
  const svc = SERVICE_DETAILS[slug];
  const [openFaq, setOpenFaq] = useState(0);
  const isComingSoon = COMING_SOON_SLUGS.has(slug);

  if (!svc) return <Navigate to="/services" replace />;

  return (
    <div className="bg-white">
      {/* Hero */}
      <section className="relative bg-[#0f1113] text-white overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-70"
          style={{ backgroundImage: `url(${svc.heroImage})` }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0f1113] via-[#0f1113]/85 to-transparent" />
        <div className="relative max-w-[1280px] mx-auto px-6 py-24 md:py-28">
          <Link
            to="/services"
            className="inline-flex items-center gap-2 text-[#c9a961] hover:text-white transition-colors text-[11px] tracking-[0.24em] font-semibold mb-6"
          >
            <ArrowLeft size={14} /> ALL SERVICES
          </Link>
          <p className="text-[11px] tracking-[0.3em] text-[#c9a961] mb-4 font-medium">AURELIA SERVICE</p>
          {isComingSoon && (
            <div
              data-testid={`coming-soon-hero-badge-${slug}`}
              className="inline-block bg-[#c9a961] text-[#141618] tracking-[0.28em] text-[11px] font-semibold px-4 py-2 mb-5"
            >
              COMING SOON
            </div>
          )}
          <h1
            className="font-serif text-5xl md:text-6xl leading-[1.05] mb-4 max-w-2xl"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {svc.title}
          </h1>
          <p className="text-white/80 text-lg max-w-lg italic" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {svc.tagline}
          </p>
        </div>
      </section>

      {isComingSoon && (
        <section className="bg-[#faf6f0] border-y border-[#c9a961]/30 py-16 md:py-20">
          <div className="max-w-[900px] mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-3">SERVICE NOTICE</p>
              <h2
                className="font-serif text-3xl md:text-4xl text-[#1c1e20] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                This service is coming soon.
              </h2>
              <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-5" />
              <p className="text-[#4a4a4a] leading-relaxed text-[15px] max-w-2xl mx-auto">
                We are not currently offering {svc.title}. The details below preview the service we are preparing.
                Join the priority list and we&rsquo;ll invite you the moment it launches.
              </p>
            </div>
            <WaitlistForm serviceName={svc.title} />
          </div>
        </section>
      )}

      {/* Intro + highlights */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">WHAT TO EXPECT</p>
            <h2
              className="font-serif text-3xl md:text-4xl text-[#1c1e20] mb-4"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Concierge-level care, expertly delivered.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mb-6" />
            <p className="text-[#4a4a4a] leading-relaxed text-[15px] mb-6">{svc.intro}</p>
            <ul className="space-y-3">
              {svc.highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-[#3c3c3c] text-[14px]">
                  <Check size={17} className="text-[#c9a961] mt-0.5 flex-shrink-0" strokeWidth={2} />
                  {h}
                </li>
              ))}
            </ul>
          </div>
          <div>
            <img
              src={svc.heroImage}
              alt={svc.title}
              className="w-full h-[440px] object-cover"
            />
          </div>
        </div>
      </section>

      {/* Process — only for services that describe on-site collection */}
      {svc.process && (
        <section className="bg-white py-20 md:py-24 border-t border-black/5">
          <div className="max-w-[1000px] mx-auto px-6">
            <div className="text-center mb-10">
              <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">ON-SITE COLLECTION</p>
              <h2
                className="font-serif text-4xl text-[#1c1e20] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                {svc.process.title}
              </h2>
              <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-6" />
              <p className="text-[#4a4a4a] leading-relaxed text-[15px] max-w-2xl mx-auto">
                {svc.process.intro}
              </p>
            </div>
            <ul className="space-y-4 max-w-3xl mx-auto">
              {svc.process.bullets.map((b, i) => (
                <li key={`${svc.process.title}-${i}`} className="flex items-start gap-4 bg-[#faf6f0] border border-black/5 p-5">
                  <Check size={20} className="text-[#c9a961] mt-0.5 flex-shrink-0" strokeWidth={2} />
                  <p className="text-[#3c3c3c] leading-relaxed text-[15px]">{b}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>
      )}

      {/* Pricing */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[1100px] mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">TRANSPARENT PRICING</p>
            <h2
              className="font-serif text-4xl text-[#1c1e20] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Options for every need.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
            <p className="text-[13px] text-[#6b6b6b] mt-4 italic">
              {svc.pricingNote || 'Prices below are starting rates. Final quote is confirmed at booking based on location and specifics.'}
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {svc.pricing.map((p) => (
              <div
                key={p.name}
                className="border border-black/10 hover:border-[#c9a961] transition-colors p-6 flex flex-col justify-between"
              >
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <Sparkles size={14} className="text-[#c9a961]" strokeWidth={1.6} />
                    <h3
                      className="font-serif text-2xl text-[#1c1e20]"
                      style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    >
                      Aurelia {p.name}
                    </h3>
                  </div>
                  <p className="text-[#6b6b6b] text-[13px]">{p.note}</p>
                </div>
                <p className="mt-4 text-[#c9a961] text-[15px] tracking-wide font-semibold">{p.price}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Service path — provider-ordered only (insured patients with doctor's order) */}
      {svc.servicePaths && (
        <section className="bg-[#faf6f0] py-20 md:py-24 border-t border-black/5" data-testid="service-paths-section">
          <div className="max-w-[1200px] mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">
                {svc.servicePaths.eyebrow}
              </p>
              <h2
                className="font-serif text-4xl md:text-5xl text-[#1c1e20] mb-4 leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                {svc.servicePaths.heading}
              </h2>
              <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-5" />
              <p className="text-[#4a4a4a] leading-relaxed max-w-[640px] mx-auto text-[15px]">
                {svc.servicePaths.intro}
              </p>
            </div>

            <div
              className={
                svc.servicePaths.options.length === 1
                  ? 'flex justify-center'
                  : 'grid grid-cols-1 md:grid-cols-2 gap-6'
              }
            >
              {svc.servicePaths.options.map((opt, i) => (
                <div
                  key={opt.title}
                  data-testid={`service-path-${i + 1}`}
                  className={`bg-white border border-black/10 hover:border-[#c9a961] transition-colors p-8 md:p-10 flex flex-col ${
                    svc.servicePaths.options.length === 1 ? 'w-full max-w-2xl' : ''
                  }`}
                >
                  <div className="flex items-center gap-3 mb-5">
                    <span className="font-serif text-[42px] text-[#c9a961] leading-none" style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}>
                      {`0${i + 1}`}
                    </span>
                    <span className="inline-block bg-[#141618] text-[#c9a961] tracking-[0.24em] text-[10px] font-semibold px-3 py-1.5">
                      {opt.tag}
                    </span>
                  </div>
                  <h3
                    className="font-serif text-2xl md:text-[28px] text-[#1c1e20] mb-3 leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {opt.title}
                  </h3>
                  <p className="text-[#4a4a4a] leading-relaxed text-[14px] mb-5">{opt.body}</p>
                  <ul className="space-y-2.5 mt-auto">
                    {opt.bullets.map((b) => (
                      <li key={b} className="flex items-start gap-3 text-[14px] text-[#3c3c3c]">
                        <Check size={14} className="text-[#c9a961] mt-0.5 flex-shrink-0" strokeWidth={2} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            {svc.servicePaths.footnote && (
              <p className="text-center text-[12px] text-[#6b6b6b] italic mt-10 max-w-xl mx-auto">
                {svc.servicePaths.footnote}
              </p>
            )}
          </div>
        </section>
      )}

      {/* Aurelia Experience — six-step luxury timeline */}
      {svc.experience && (
        <section className="bg-white py-20 md:py-24 border-t border-black/5" data-testid="aurelia-experience-section">
          <div className="max-w-[1100px] mx-auto px-6">
            <div className="text-center mb-14">
              <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">A SIGNATURE JOURNEY</p>
              <h2
                className="font-serif text-4xl md:text-5xl text-[#1c1e20] mb-3 leading-tight"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                {svc.experience.title}
              </h2>
              <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
            </div>

            <ol className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-10">
              {svc.experience.steps.map((step, i) => (
                <li
                  key={step.title}
                  data-testid={`experience-step-${i + 1}`}
                  className="relative pl-14 md:pl-16"
                >
                  <span
                    className="absolute left-0 top-0 font-serif text-[36px] md:text-[42px] leading-none text-[#c9a961]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                    aria-hidden="true"
                  >
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span className="absolute left-[54px] md:left-[62px] top-3 w-8 h-[1px] bg-[#c9a961]/50 hidden md:block" aria-hidden="true" />
                  <h3
                    className="font-serif text-[24px] text-[#1c1e20] mb-2 leading-tight"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {step.title}
                  </h3>
                  <p className="text-[#4a4a4a] leading-relaxed text-[15px]">{step.body}</p>
                </li>
              ))}
            </ol>

            {svc.experience.closer && (
              <div className="mt-16 text-center border-t border-black/10 pt-12">
                <p
                  className="font-serif text-3xl md:text-4xl text-[#1c1e20] italic leading-tight mb-2"
                  style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                >
                  {svc.experience.closer.primary}
                </p>
                <p className="text-[13px] tracking-[0.28em] text-[#c9a961] font-medium">
                  {svc.experience.closer.secondary.toUpperCase()}
                </p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* Test Categories — full menu list (mobile phlebotomy) */}
      {svc.testCategories && (
        <section className="bg-[#faf6f0] py-20 md:py-24">
          <div className="max-w-[1100px] mx-auto px-6">
            <div className="text-center mb-12">
              <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">FULL TEST MENU</p>
              <h2
                className="font-serif text-4xl text-[#1c1e20] mb-3"
                style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
              >
                Comprehensive testing panels.
              </h2>
              <div className="w-16 h-[2px] bg-[#c9a961] mx-auto mb-4" />
              <p className="text-[#4a4a4a] leading-relaxed text-[14px] max-w-2xl mx-auto">
                We can collect samples for tests across every major category below. If you don’t see what you need, just ask.
              </p>
            </div>
            <div className="bg-white border border-black/5 p-6 md:p-8">
              <ul className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-2">
                {svc.testCategories.map((c) => (
                  <li key={c} className="flex items-start gap-2 py-1.5 border-b border-black/5 last:border-b-0">
                    <Check size={14} className="text-[#c9a961] mt-1 flex-shrink-0" strokeWidth={2} />
                    <span className="text-[#3c3c3c] text-[14px]">{c}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      )}

      {/* Preparation */}
      <section className="bg-[#f5f0ea] py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">BEFORE YOUR APPOINTMENT</p>
            <h2
              className="font-serif text-4xl text-[#1c1e20] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              How to prepare.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
          </div>
          <ol className="space-y-4">
            {svc.prep.map((step, i) => (
              <li key={`prep-${i}-${step.slice(0, 24)}`} className="flex items-start gap-4 bg-white border border-black/5 p-5">
                <div className="w-8 h-8 rounded-full bg-[#c9a961] text-[#141618] flex items-center justify-center font-semibold text-[13px] flex-shrink-0">
                  {i + 1}
                </div>
                <p className="text-[#3c3c3c] leading-relaxed text-[15px] pt-1">{step}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* FAQs */}
      <section className="bg-white py-20 md:py-24">
        <div className="max-w-[900px] mx-auto px-6">
          <div className="text-center mb-10">
            <p className="text-[11px] tracking-[0.3em] text-[#c9a961] font-medium mb-4">FREQUENTLY ASKED</p>
            <h2
              className="font-serif text-4xl text-[#1c1e20] mb-3"
              style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
            >
              Common questions.
            </h2>
            <div className="w-16 h-[2px] bg-[#c9a961] mx-auto" />
          </div>
          <div className="space-y-3">
            {svc.faqs.map((f, i) => (
              <div
                key={f.q}
                className={`border transition-colors ${openFaq === i ? 'border-[#c9a961]' : 'border-black/10'}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full text-left px-6 py-5 flex items-center justify-between gap-4"
                >
                  <span
                    className="font-serif text-lg md:text-xl text-[#1c1e20]"
                    style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
                  >
                    {f.q}
                  </span>
                  <span className="w-8 h-8 rounded-full border border-[#c9a961] flex items-center justify-center text-[#c9a961] flex-shrink-0">
                    {openFaq === i ? <Minus size={14} /> : <Plus size={14} />}
                  </span>
                </button>
                {openFaq === i && (
                  <div className="px-6 pb-6 text-[#4a4a4a] leading-relaxed text-[15px] border-t border-black/5 pt-4">
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative bg-[#141618] text-white">
        <div className="max-w-[1280px] mx-auto px-6 py-16 grid grid-cols-1 md:grid-cols-2 items-center gap-8">
          <h3
            className="font-serif text-3xl md:text-4xl leading-tight"
            style={{ fontFamily: "'Cormorant Garamond', serif", fontWeight: 500 }}
          >
            {isComingSoon
              ? `${svc.title} — coming soon.`
              : `Ready to book your ${svc.title.toLowerCase()}?`}
          </h3>
          <div>
            <p className="text-white/80 mb-5 max-w-md">
              {isComingSoon
                ? 'We are not offering this service yet. Reach out and we\u2019ll notify you the moment it launches.'
                : 'A member of our team will confirm your appointment within the hour.'}
            </p>
            <Link
              to="/contact"
              data-testid={`service-detail-cta-${slug}`}
              className="inline-block bg-[#c9a961] hover:bg-[#b89751] transition-colors duration-300 text-[#141618] tracking-[0.22em] text-[12px] font-semibold px-7 py-3.5"
            >
              {isComingSoon ? 'CONTACT US' : 'BOOK YOUR APPOINTMENT'}
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ServiceDetail;
